import type { ComputedRef, InjectionKey } from 'vue'
import type { Message, MessageContent } from '../types/interfaces'
import { MessageSourceType, MessageType } from '../types/enums'

export type ChatMediaType = 'image' | 'video'

export interface ChatMediaItem {
  id: string
  type: ChatMediaType
  url: string
  fileName?: string | null
  caption?: string | null
}

export const chatMediaItemsKey: InjectionKey<ComputedRef<ChatMediaItem[]>> =
  Symbol('chatMediaItems')

const URL_REGEX = /(https?:\/\/[^\s]+)/i
const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg']
const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.mov', '.m4v', '.avi', '.mpeg', '.mpg', '.3gp', '.3gpp']

const extractUrl = (value?: string | null) => value?.match(URL_REGEX)?.[0] ?? null

const getUrlPath = (url: string) => {
  try {
    return new URL(url).pathname.toLowerCase()
  } catch {
    return url.split('?')[0]?.toLowerCase() ?? url.toLowerCase()
  }
}

const hasExtension = (url: string, extensions: string[]) => {
  const path = getUrlPath(url)
  return extensions.some((extension) => path.endsWith(extension))
}

const isMediaTypeFallbackText = (value: string, mediaType: MessageType) =>
  value.trim().toLowerCase() === mediaType

const resolveMedia = (message: Message): Omit<ChatMediaItem, 'id'> | null => {
  const content = message.content
  const textContent = content.content || content.text || ''
  const url = content.url || extractUrl(textContent)
  const contentType = (content.content_type || content.file_content_type || '').toLowerCase()

  if (!url) return null

  const isImage =
    content.type === MessageType.IMAGE ||
    contentType.startsWith('image/') ||
    hasExtension(url, IMAGE_EXTENSIONS)
  const isVideo =
    content.type === MessageType.VIDEO ||
    contentType.startsWith('video/') ||
    hasExtension(url, VIDEO_EXTENSIONS)

  if (!isImage && !isVideo) return null

  const mediaType = isImage ? MessageType.IMAGE : MessageType.VIDEO
  const trimmedTextContent = textContent.trim()
  const isOutgoing =
    message.source_type === MessageSourceType.SALIENTE ||
    message.source_type === MessageSourceType.TEMPLATE
  const isFileNameFallbackCaption = Boolean(
    isOutgoing &&
      isImage &&
      content.file_name &&
      (trimmedTextContent === content.file_name ||
        trimmedTextContent.startsWith(`${content.file_name} (`)),
  )
  const rawCaption = content.caption || null
  const caption =
    rawCaption && !isMediaTypeFallbackText(rawCaption, mediaType)
      ? rawCaption
      : !trimmedTextContent ||
          trimmedTextContent === url ||
          isFileNameFallbackCaption ||
          isMediaTypeFallbackText(trimmedTextContent, mediaType)
        ? null
        : textContent

  return {
    type: isImage ? 'image' : 'video',
    url,
    fileName: content.file_name,
    caption,
  }
}

export const buildChatMediaItems = (messages: Message[]) =>
  messages.reduce<ChatMediaItem[]>((items, message) => {
    const media = resolveMedia(message)

    if (media) {
      items.push({
        id: message.id,
        ...media,
      })
    }

    return items
  }, [])
